import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import DateField from '../../../core/components/Fields/DateField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_API_INTEGRATIONS_PATH } from '../../constants.ts';
import { GET_API_INTEGRATIONS, UPDATE_API_INTEGRATION } from '../../queries.ts';

const FORM_DEFAULT_VALUES = {
    name: '',
    expiresAt: null,
    allChannels: false,
    orderChannels: [],
    orderConfirmationChannels: [],
    invoiceChannels: [],
    rfqChannels: [],
    deliveryNoteChannels: [],
    listOfServicesChannels: [],
    universalChannels: [],
};

const ApiIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const { data: integrationsData, refetch } = useQuery(GET_API_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = integrationsData?.apiIntegrations?.find((i) => i.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });

    const orderChannels = channelData?.orderProcessingChannels || [];
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];
    const invoiceChannels = channelData?.invoiceProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];
    const deliveryNoteChannels = channelData?.deliveryNoteProcessingChannels || [];
    const listOfServicesChannels = channelData?.listOfServicesProcessingChannels || [];
    const universalChannels = channelData?.universalProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;
    const allChannelsValue = form.watch('allChannels');
    const backTooltipText = `${t('backTooltipText')} ${t('apiIntegration.title')}`;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                expiresAt: integration.expiresAt ? integration.expiresAt.split('T')[0] : null,
                allChannels: integration.allChannels || false,
                orderChannels: integration.orderChannels.map((c) => c.id),
                orderConfirmationChannels: integration.orderConfirmationChannels.map((c) => c.id),
                invoiceChannels: integration.invoiceChannels.map((c) => c.id),
                rfqChannels: integration.rfqChannels.map((c) => c.id),
                deliveryNoteChannels: integration.deliveryNoteChannels.map((c) => c.id),
                listOfServicesChannels: integration.listOfServicesChannels.map((c) => c.id),
                universalChannels: integration.universalChannels.map((c) => c.id),
            });
        }
    }, [integration]);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_API_INTEGRATION);

    const handleSubmit = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    apiIntegrationId: integration.id,
                    name: formData.name,
                    expiresAt: formData.expiresAt,
                    allChannels: formData.allChannels,
                    orderChannels: formData.orderChannels,
                    orderConfirmationChannels: formData.orderConfirmationChannels,
                    invoiceChannels: formData.invoiceChannels,
                    rfqChannels: formData.rfqChannels,
                    deliveryNoteChannels: formData.deliveryNoteChannels,
                    listOfServicesChannels: formData.listOfServicesChannels,
                    universalChannels: formData.universalChannels,
                },
            });
            await refetch();
            publishToast({ description: t('apiIntegration.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_API_INTEGRATIONS_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('apiIntegration.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('apiIntegration.title'), to: url(CUSTOMER_API_INTEGRATIONS_PATH) },
                            { label: integration?.name || t('apiIntegration.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleSubmit)}
                        title={t('apiIntegration.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('apiIntegration.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="expiresAt"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('apiIntegration.form.expiresAt')} error={formErrors.expiresAt}>
                                    <DateField {...field} onValueChange={(value) => field.onChange(value)} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="allChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('apiIntegration.form.allChannels')} error={formErrors.allChannels}>
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />

                        {!allChannelsValue && (
                            <>
                                <Controller
                                    name="orderChannels"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('apiIntegration.form.orderChannels')}
                                            error={formErrors.orderChannels}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="orderConfirmationChannels"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('apiIntegration.form.orderConfirmationChannels')}
                                            error={formErrors.orderConfirmationChannels}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={orderConfirmationChannels.map((c) => ({
                                                    value: c.id,
                                                    label: c.name,
                                                }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="invoiceChannels"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('apiIntegration.form.invoiceChannels')}
                                            error={formErrors.invoiceChannels}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={invoiceChannels.map((c) => ({ value: c.id, label: c.name }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="rfqChannels"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('apiIntegration.form.rfqChannels')}
                                            error={formErrors.rfqChannels}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={rfqChannels.map((c) => ({ value: c.id, label: c.name }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="deliveryNoteChannels"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('apiIntegration.form.deliveryNoteChannels')}
                                            error={formErrors.deliveryNoteChannels}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={deliveryNoteChannels.map((c) => ({
                                                    value: c.id,
                                                    label: c.name,
                                                }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="listOfServicesChannels"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('apiIntegration.form.listOfServicesChannels')}
                                            error={formErrors.listOfServicesChannels}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={listOfServicesChannels.map((c) => ({
                                                    value: c.id,
                                                    label: c.name,
                                                }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />

                                <Controller
                                    name="universalChannels"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('apiIntegration.form.universalChannels')}
                                            error={formErrors.universalChannels}
                                        >
                                            <CheckboxGroup
                                                {...field}
                                                options={universalChannels.map((c) => ({ value: c.id, label: c.name }))}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                            />
                                        </FormField>
                                    )}
                                />
                            </>
                        )}

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_API_INTEGRATIONS_PATH)}>
                                    {t('apiIntegration.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default ApiIntegrationEditPage;
